import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Tooltip, FormFeedback } from "reactstrap";

const MultiSelectDropdown = ({
    label = "Select Option", 
    required = false, 
    layout = "below", // Layout style (inline or below)
    options = [],
    defaultSelectedValues = [], 
    maxSelections = null, 
    name = "multiSelectDropdown", 
    id = "multiSelectDropdown", 
    onChange = () => {}, 
    onBlur = () => {}, 
    placeholder = "Please select an option", 
    showHelp = false,
    helpText = "Please select options.", 
    requiredErrorMessage = "You must select at least one option.", 
    maxSelectionErrorMessage = "You can select up to [n] options.", 
    customError = null, 
    isClearable = true, 
}) => {
    const [selectedValues, setSelectedValues] = useState([]);
    const [errors, setErrors] = useState({});
    const [tooltipOpen, setTooltipOpen] = useState(false);

    // Set default selected options based on the values passed
    useEffect(() => {
        if (defaultSelectedValues.length > 0) {
            const defaultSelectedOptions = options.filter(option =>
                defaultSelectedValues.includes(option.value)
            );
            const formattedOptions = defaultSelectedOptions.map(option => ({
                value: option.value,
                label: option.value,
            }));
            setSelectedValues(formattedOptions); 
        }
    }, [defaultSelectedValues, options]);

    // Validation function
    const validateInput = (values) => {
        const newErrors = {};
        if (required && values.length === 0) {
            newErrors.required = requiredErrorMessage;
        }
        if (maxSelections && values.length > maxSelections) {
            newErrors.maxSelections = maxSelectionErrorMessage.replace("[n]", maxSelections);
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0; // Return true if no errors
    };

    // Handle option select/deselect
    const handleOptionSelect = (selectedOptions) => {
        const selected = selectedOptions || [];
        validateInput(selected); 
        setSelectedValues(selected); 
        onChange(selected); 
    };

    return (
        <div className={layout === "inline" ? "d-flex align-items-center" : ""}>
            <label htmlFor={id} className="d-flex" style={{ width: layout === "inline" ? "20%" : "100%" }}>
                {label}
                {required && <span className="text-danger ml-1">*</span>}
                {showHelp && (
                    <span id="helpTooltip" className="px-2">
                        <i className="ri-question-line" style={{ cursor: 'pointer', fontSize: '16px' }} />
                    </span>
                )}
            </label>
            {showHelp && (
                <Tooltip
                    placement="right"
                    isOpen={tooltipOpen}
                    target="helpTooltip"
                    toggle={() => setTooltipOpen(!tooltipOpen)}
                >
                    {helpText}
                </Tooltip>
            )}
            <div className="w-100">
                <Select
                    isMulti 
                    name={name}
                    id={id}
                    value={selectedValues}
                    onChange={handleOptionSelect} 
                    onBlur={() => validateInput(selectedValues)} 
                    options={options.map(option => ({ value: option.value, label: option.value }))}
                    placeholder={placeholder} 
                    isSearchable
                    closeMenuOnSelect={false} 
                    classNamePrefix="react-select"
                    isClearable={isClearable} 
                    className={Object.keys(errors).length > 0 ? 'is-invalid' : ''} 
                />
                {errors.required && (
                    <FormFeedback type="invalid">
                        {customError || errors.required}
                    </FormFeedback>
                )}
                {errors.maxSelections && (
                    <FormFeedback type="invalid">
                        {customError || errors.maxSelections}
                    </FormFeedback>
                )}
            </div>
        </div>
    );
};

export default MultiSelectDropdown;
